import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Divider,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { Edit, Save, Cancel } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { useModulePermissions } from 'hooks/useModulePermissions';
import { WhatsAppBusinessConfig } from 'modules/Interfaces/Company/Company';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';

export const WhatsAppBusiness: React.FC = () => {
  const theme = useTheme();
  const user = useSelector((state: RootState) => state.authentication.user);
  const { hasInstantUpdateAccess, getInstantUpdateRole, isUserSuperAdmin } = useModulePermissions();

  const [configuration, setConfiguration] = useState<WhatsAppBusinessConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [onboarding, setOnboarding] = useState<boolean>(false);
  const [billing, setBilling] = useState<any | null>(null);
  const [billingLoading, setBillingLoading] = useState<boolean>(false);
  const [numbers, setNumbers] = useState<any[] | null>(null);
  const [templates, setTemplates] = useState<{ summary?: any; recent?: any[] } | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [testDialogOpen, setTestDialogOpen] = useState<boolean>(false);
  const [testDestination, setTestDestination] = useState<string>('');
  const [testMessage, setTestMessage] = useState<string>('');
  const [testLoading, setTestLoading] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<string>('');

  const clientConfig = useSelector((state: RootState) => state.clientConfig.data);

  const channelKey = 'whatsappbusiness';
  const FB_APP_ID = clientConfig?.facebookAppId || '';
  const FB_VERSION = clientConfig?.facebookApiVersion || 'v23.0';
  const FB_REDIRECT = clientConfig?.fbEmbeddedRedirectUri || '';
  const userRole = getInstantUpdateRole();
  const canEdit = isUserSuperAdmin || userRole === 'admin';
  const canView = hasInstantUpdateAccess() || isUserSuperAdmin;

  if (!canView) {
    return (
      <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          Access Denied
        </Typography>
        <Typography variant="body1" color="textSecondary">
          You don't have permission to access WhatsApp Business configuration.
        </Typography>
      </Box>
    );
  }

  const fetchConfiguration = async () => {
    if (!user?.companyId) {
      Logger.warn("WhatsAppBusiness.tsx : fetchConfiguration()", "User companyId is not available.");
      return;
    }
    setLoading(true);
    try {
      const data = await apiClient.get(`/configuration/${user.companyId}/${channelKey}`);
      const configData = data.data || null;
      setConfiguration(configData);
      Logger.info("WhatsAppBusiness.tsx : fetchConfiguration()", `Fetched configuration: ${JSON.stringify(configData)}`);
    } catch (error) {
      Logger.error("WhatsAppBusiness.tsx : fetchConfiguration()", `Failed to fetch configuration: ${error}`);
      setSnackbar({ open: true, message: `Failed to load configuration: ${error instanceof Error ? error.message : String(error)}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfiguration();
  }, [user?.companyId]);

  const handleOpenDialog = () => {
    if (!canEdit) return;
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    fetchConfiguration();
  };

  const handleSaveConfiguration = async (configData: WhatsAppBusinessConfig) => {
    if (!user?.companyId || !canEdit) return;

    try {
      setLoading(true);
      await apiClient.put(`/configuration/${user.companyId}/${channelKey}`, configData);
      setSnackbar({ open: true, message: 'WhatsApp Business configuration saved successfully!', severity: 'success' });
      handleCloseDialog();
    } catch (error) {
      Logger.error("WhatsAppBusiness.tsx : handleSaveConfiguration()", `Failed to save configuration: ${error}`);
      setSnackbar({ open: true, message: `Operation failed: ${error instanceof Error ? error.message : String(error)}`, severity: 'error' });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Launch Embedded Signup in a popup
  const handleStartEmbeddedSignup = () => {
    if (!FB_APP_ID || !FB_REDIRECT) {
      setSnackbar({ open: true, message: 'Facebook App ID or Redirect URI not configured', severity: 'error' });
      return;
    }
    setOnboarding(true);
    const params = new URLSearchParams();
    params.set('client_id', FB_APP_ID);
    params.set('redirect_uri', FB_REDIRECT);
    params.set('response_type', 'code');
    params.set('state', 'whatsappbusiness');
    // Required scopes for WhatsApp Business Embedded Signup
    // Ref: https://developers.facebook.com/docs/whatsapp/embedded-signup/ (Permissions)
    params.set('scope', 'whatsapp_business_management,whatsapp_business_messaging');
    // Optional but recommended for popup UX
    params.set('display', 'popup');
    const url = `https://www.facebook.com/${FB_VERSION}/dialog/oauth?${params.toString()}`;
    const w = 700;
    const h = 800;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    window.open(url, 'wa_embedded_signup', `width=${w},height=${h},left=${left},top=${top}`);
  };

  // Listen for code from popup and exchange via server
  useEffect(() => {
    const listener = async (event: MessageEvent) => {
      const data = event.data as any;
      if (data && data.type === 'WHATSAPP_EMBEDDED_SIGNUP_CODE' && data.code) {
        try {
          if (!user?.companyId) return;
          await apiClient.post(`/configuration/${user.companyId}/whatsappbusiness/exchange`, { code: data.code });
          setSnackbar({ open: true, message: 'Onboarding successful. Access token saved.', severity: 'success' });
          refreshAfterExchange();
        } catch (e) {
          setSnackbar({ open: true, message: `Failed to exchange code: ${e instanceof Error ? e.message : String(e)}`, severity: 'error' });
        } finally {
          setOnboarding(false);
        }
      }
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [user?.companyId]);

  const refreshAfterExchange = () => {
    fetchConfiguration();
    fetchBilling();
    fetchNumbers();
    fetchTemplates();
    fetchProfile();
  };

  const fetchBilling = async () => {
    try {
      if (!user?.companyId) return;
      setBillingLoading(true);
      const data = await apiClient.get(`/configuration/${user.companyId}/whatsappbusiness/billing`);
      setBilling(data.data || null);
    } catch (e) {
      setBilling(null);
    } finally {
      setBillingLoading(false);
    }
  };

  useEffect(() => {
    fetchBilling();
    fetchNumbers();
    fetchTemplates();
    fetchProfile();
  }, [user?.companyId]);

  const fetchNumbers = async () => {
    try {
      if (!user?.companyId) return;
      const data = await apiClient.get(`/configuration/${user.companyId}/whatsappbusiness/numbers`);
      setNumbers(data.data?.numbers || []);
    } catch {
      setNumbers(null);
    }
  };

  const fetchTemplates = async () => {
    try {
      if (!user?.companyId) return;
      const data = await apiClient.get(`/configuration/${user.companyId}/whatsappbusiness/templates`);
      setTemplates(data.data || null);
    } catch {
      setTemplates(null);
    }
  };

  const fetchProfile = async () => {
    try {
      if (!user?.companyId) return;
      const data = await apiClient.get(`/configuration/${user.companyId}/whatsappbusiness/profile`);
      setProfile(data.data || null);
    } catch {
      setProfile(null);
    }
  };

  return (
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
      {loading && (
        <Backdrop sx={{ color: theme.palette.secondary.main, zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        WhatsApp Business Integration
      </Typography>

      <Box display="flex" flexWrap="wrap" gap={3} sx={{ maxWidth: 800, margin: 'auto' }}>
        <Box sx={{ width: '100%' }}>
          <Card sx={{ p: 3, borderRadius: '12px', boxShadow: 3, height: '100%' }}>
            <CardContent>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="h5" sx={{ color: theme.palette.heading?.main ?? theme.palette.secondary.main }}>
                  Configured Credentials
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {canEdit && (
                  <Button variant="outlined" color="secondary" startIcon={<Edit />} onClick={handleOpenDialog}>
                    {configuration ? 'Edit Configuration' : 'Add Configuration'}
                  </Button>
                )}
                {canEdit && (
                  <Button variant="contained" color="success" onClick={handleStartEmbeddedSignup} disabled={onboarding}>
                    {onboarding ? 'Starting...' : 'Start Embedded Signup'}
                  </Button>
                )}
                {canEdit && configuration?.accessToken && (
                  <Button variant="outlined" color="primary" onClick={() => {
                    setTestDestination(configuration?.testRecipientNumber || '');
                    setTestMessage(configuration?.testMessage || 'Hello from CampaignKing test!');
                    setTestResult('');
                    setTestDialogOpen(true);
                  }}>
                    Send Test Message
                  </Button>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {configuration ? (
                <Box display="flex" flexWrap="wrap" gap={3}>
                  <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                    <TextField
                      label="Access Token"
                      value={configuration.accessToken ? '********' : 'Not Set'}
                      fullWidth
                      margin="normal"
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                    <TextField
                      label="Phone Number ID"
                      value={configuration.phoneNumberId || 'Not Set'}
                      fullWidth
                      margin="normal"
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                    <TextField
                      label="Business Account ID"
                      value={configuration.businessAccountId || 'Not Set'}
                      fullWidth
                      margin="normal"
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    <TextField
                      label="Last Updated"
                      value={configuration.lastUpdated ? new Date(configuration.lastUpdated).toLocaleString() : 'Not Set'}
                      fullWidth
                      margin="normal"
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      size="small"
                    />
                  </Box>

                  {/* Billing panel */}
                  <Box sx={{ width: '100%' }}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>Billing Information</Typography>
                    {billingLoading ? (
                      <Typography variant="body2">Loading billing info...</Typography>
                    ) : billing ? (
                      <Box display="flex" flexWrap="wrap" gap={3}>
                        <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                          <TextField label="WABA ID" value={billing.wabaId || ''} fullWidth margin="normal" InputProps={{ readOnly: true }} variant="outlined" size="small" />
                        </Box>
                        <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                          <TextField label="WABA Name" value={billing.account?.name || ''} fullWidth margin="normal" InputProps={{ readOnly: true }} variant="outlined" size="small" />
                        </Box>
                        <Box sx={{ width: '100%' }}>
                          <TextField label="Phone Numbers" value={(billing.phoneNumbers || []).map((n: any) => n.display_phone_number || n.id).join(', ')} fullWidth margin="normal" InputProps={{ readOnly: true }} variant="outlined" size="small" />
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">Billing info not available.</Typography>
                    )}
                  </Box>

                  {/* Numbers panel */}
                  <Box sx={{ width: '100%' }}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>Phone Numbers</Typography>
                    {numbers && numbers.length > 0 ? (
                      <Box display="flex" flexWrap="wrap" gap={3}>
                        {numbers.map((n: any) => (
                          <Box key={n.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                            <TextField label="Phone" value={n.display_phone_number || n.id} fullWidth margin="normal" InputProps={{ readOnly: true }} variant="outlined" size="small" />
                            {n.quality_rating && (
                              <TextField label="Quality Rating" value={n.quality_rating} fullWidth margin="normal" InputProps={{ readOnly: true }} variant="outlined" size="small" />
                            )}
                            {n.code_verification_status && (
                              <TextField label="Verification" value={n.code_verification_status} fullWidth margin="normal" InputProps={{ readOnly: true }} variant="outlined" size="small" />
                            )}
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">No phone numbers found.</Typography>
                    )}
                  </Box>

                  {/* Templates panel */}
                  <Box sx={{ width: '100%' }}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>Templates</Typography>
                    {templates ? (
                      <Box>
                        <Typography variant="subtitle2">Summary by Status</Typography>
                        <pre style={{ whiteSpace: 'pre-wrap', background:'#f7f7f7', padding:8, borderRadius:6 }}>{JSON.stringify(templates.summary?.byStatus || {}, null, 2)}</pre>
                        <Typography variant="subtitle2" sx={{ mt:1 }}>Summary by Category</Typography>
                        <pre style={{ whiteSpace: 'pre-wrap', background:'#f7f7f7', padding:8, borderRadius:6 }}>{JSON.stringify(templates.summary?.byCategory || {}, null, 2)}</pre>
                        <Typography variant="subtitle2" sx={{ mt:1 }}>Recent</Typography>
                        <pre style={{ whiteSpace: 'pre-wrap', background:'#f7f7f7', padding:8, borderRadius:6 }}>{JSON.stringify(templates.recent || [], null, 2)}</pre>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">No template data.</Typography>
                    )}
                  </Box>

                  {/* Profile panel */}
                  <Box sx={{ width: '100%' }}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>Business Profile</Typography>
                    {profile ? (
                      <pre style={{ whiteSpace: 'pre-wrap', background:'#f7f7f7', padding:8, borderRadius:6 }}>{JSON.stringify(profile, null, 2)}</pre>
                    ) : (
                      <Typography variant="body2" color="textSecondary">No profile data.</Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 5 }}>
                  No WhatsApp Business configuration found. {canEdit ? 'Click "Add Configuration" to get started.' : 'Contact your administrator to configure WhatsApp Business.'}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {canEdit && (
        <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="md">
          <DialogTitle>{configuration ? 'Edit WhatsApp Business Configuration' : 'Add New WhatsApp Business Configuration'}</DialogTitle>
          <DialogContent>
            <ConfigurationForm
              configuration={configuration}
              onSave={handleSaveConfiguration}
              onCancel={handleCloseDialog}
            />
          </DialogContent>
          <DialogActions>
            <Button 
              variant="outlined"
              color="secondary" 
              onClick={handleCloseDialog} 
              startIcon={<Cancel />}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => document.getElementById('whatsappbusiness-configuration-form')?.dispatchEvent(new Event('submit', { bubbles: true }))}
              startIcon={<Save />} 
              variant="contained" 
              color="success"
            >
              Save Configuration
            </Button>
          </DialogActions>
        </Dialog>
      )}


      {canEdit && (
        <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Send Test WhatsApp Message</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <TextField
                label="Test Destination Number"
                value={testDestination}
                onChange={(e) => setTestDestination(e.target.value)}
                fullWidth
                margin="normal"
                placeholder="e.g. 15551234567"
              />
              <TextField
                label="Sample Message"
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                fullWidth
                margin="normal"
                multiline
                rows={3}
              />
              {testResult && (
                <Alert sx={{ mt: 2 }} severity={testResult.startsWith('Success') ? 'success' : 'error'}>
                  {testResult}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTestDialogOpen(false)} startIcon={<Cancel />}>Close</Button>
            <Button
              variant="contained"
              color="success"
              disabled={testLoading || !testDestination || !testMessage}
              onClick={async () => {
                if (!user?.companyId) return;
                setTestLoading(true);
                setTestResult('');
                try {
                  const resp = await apiClient.post(`/configuration/${user.companyId}/whatsappbusiness/test-message`, { to: testDestination, message: testMessage });
                  const id = resp?.data?.id || 'unknown';
                  setTestResult(`Success. Message ID: ${id}`);
                } catch (e: any) {
                  const msg = (e && e.message) ? e.message : String(e);
                  if (msg.includes('EXPIRED_TOKEN')) {
                    setTestResult('Error: Access token expired. Please run Embedded Signup again.');
                  } else {
                    setTestResult(`Error: ${msg}`);
                  }
                } finally {
                  setTestLoading(false);
                }
              }}
            >
              {testLoading ? 'Sending...' : 'Send Test'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

interface ConfigurationFormProps {
  configuration: WhatsAppBusinessConfig | null;
  onSave: (config: WhatsAppBusinessConfig) => Promise<void>;
  onCancel: () => void;
}

const ConfigurationForm: React.FC<ConfigurationFormProps> = ({ configuration, onSave, onCancel: _onCancel }) => {
  const [formData, setFormData] = useState<WhatsAppBusinessConfig>({
    accessToken: configuration?.accessToken || '',
    phoneNumberId: configuration?.phoneNumberId || '',
    businessAccountId: configuration?.businessAccountId || '',
    testRecipientNumber: configuration?.testRecipientNumber || '',
    testMessage: configuration?.testMessage || '',
    isActive: configuration?.isActive || false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <Box component="form" id="whatsappbusiness-configuration-form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <TextField
        label="Access Token"
        value={formData.accessToken || ''}
        onChange={(e) => setFormData({ ...formData, accessToken: e.target.value })}
        fullWidth
        margin="normal"
        required
        variant="outlined"
        type="password"
      />
      <TextField
        label="Phone Number ID"
        value={formData.phoneNumberId || ''}
        onChange={(e) => setFormData({ ...formData, phoneNumberId: e.target.value })}
        fullWidth
        margin="normal"
        required
        variant="outlined"
      />
      <TextField
        label="Business Account ID"
        value={formData.businessAccountId || ''}
        onChange={(e) => setFormData({ ...formData, businessAccountId: e.target.value })}
        fullWidth
        margin="normal"
        required
        variant="outlined"
      />
      <TextField
        label="Test Recipient Number"
        value={formData.testRecipientNumber || ''}
        onChange={(e) => setFormData({ ...formData, testRecipientNumber: e.target.value })}
        fullWidth
        margin="normal"
        variant="outlined"
        placeholder="e.g. 15551234567"
      />
      <TextField
        label="Test Message"
        value={formData.testMessage || ''}
        onChange={(e) => setFormData({ ...formData, testMessage: e.target.value })}
        fullWidth
        margin="normal"
        variant="outlined"
        multiline
        rows={3}
      />
    </Box>
  );
};


